import * as React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import useCurrentUser from "src/core/users/hooks/useCurrentUser";
import { CreditStatus } from "src/modules/support/api/support.api";

import { useCreditRequestContext } from "../context/CreditRequestContext";
import { creditRequestCount } from "../utils/credit-request-count";
import { creditRequestedPrice } from "../utils/credit-requested-price";
import { ErrorNotice } from "../../../core/shared/components/ErrorNotice";

interface CreditRequestStatusBarProps {
  creditWholeOrder?: boolean;
  canAmendQuantities: boolean;
}
/**
 * Status bar to appear below credit request selections
 */
export const CreditRequestStatusBar: React.ReactFCWithChildren<CreditRequestStatusBarProps> = ({ creditWholeOrder, canAmendQuantities }) => {
  const [
    {
      credits, mode, order, isLoading, supportTicket
    },
    { saveCredits }
  ] = useCreditRequestContext();

  const totalRequested = creditRequestCount(credits, "requested");
  const totalApproved = creditRequestCount(credits, "approved");
  const [ { currentRole } ] = useCurrentUser();
  const [ error, setError ] = React.useState<string | null>(null);

  const totalRequestedPrice = React.useMemo(() => {
    if (order) {
      return creditRequestedPrice(order, credits, "requested");
    }

    return "0";
  }, [ credits, order ]);

  const totalApprovedPrice = React.useMemo(() => {
    if (order) {
      return creditRequestedPrice(order, credits, "approved");
    }

    return "0";
  }, [ credits, order ]);

  React.useEffect(() => {
    // const creditsForTicket = _.filter(credits, cred => cred.localId || cred.supportTicketId === supportTicket?.id);

    if (creditWholeOrder && !supportTicket?.id) {
      if (credits.length) {
        setError("Cannot use this ticket type after already reporting product issues on another support ticket");

        return;
      }
    }

    setError(null);
  }, [
    creditWholeOrder,
    credits,
    supportTicket?.id
  ]);

  return (
    <CreditStatusBarWrapper direction="vertical">
      <ErrorNotice
        message={error}
        type="error"
      />

      {canAmendQuantities && (
        <Grid.Bounds
          className="credit-status-values"
          direction="horizontal"
        >
          <StatusInfoColumn
            title="Total requested"
            value={totalRequested.toString()}
          />

          {currentRole?.type === "admin" && (
            <StatusInfoColumn
              title="Credit requested"
              value={totalRequestedPrice}
            />
          )}

          {mode === "review" || supportTicket?.creditStatus === CreditStatus.AutoReviewed || supportTicket?.creditStatus === CreditStatus.Reviewed && (
            <Grid.Bounds className="credit-approval-values">
              <StatusInfoColumn
                title="Total accepted"
                value={totalApproved.toString()}
              />

              <StatusInfoColumn
                title="Total credited"
                value={totalApprovedPrice}
              />

            </Grid.Bounds>
          )}
        </Grid.Bounds>
      )}

      {([ "edit", "review" ].includes(mode) || !supportTicket?.id) && (
        <Grid.Bounds
          className="credit-status-actions"
          direction="horizontal"
        >
          <Button
            loading={isLoading}
            onClick={() => saveCredits()}
            disabled={!!error}
          >
            Save
          </Button>
        </Grid.Bounds>
      )}
    </CreditStatusBarWrapper>
  );
};

const CreditStatusBarWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.margins.sm};

    .credit-status-values {
      padding: ${theme.spacing.padding.md};  
      justify-content: flex-end;
    }
    .credit-approval-values {
      background: ${theme.colors.white};
    }

    .status-col {
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.lg}; 
    }

    .credit-status-actions {
      background: ${theme.colors.white};
      padding: ${theme.spacing.padding.md};  
      justify-content: flex-end;
    }
  `}
`;

interface StatusInfoColumnProps {
  title: string;
  value: string;
}

const StatusInfoColumn: React.ReactFCWithChildren<StatusInfoColumnProps> = ({ title, value }) => {
  return (
    <Grid.Bounds
      direction="vertical"
      className="status-col"
    >
      <Text>
        {title}
      </Text>

      <Text fontSize="h3">
        {value}
      </Text>

    </Grid.Bounds>
  );
};
